import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  UserIcon,
  Cog8ToothIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { useGetUserDetailsQuery } from "../../../../../store/admin/professionalUserInfo";

const ProfessionalAdminSettingsAsideBar = ({ setShowAsideBar }) => {
  const location = useLocation()
  const { professionalId } = location?.state ?? {};
  const { data: professionalInfo } =
    useGetUserDetailsQuery(professionalId);

  const [activeLink, setActiveLink] = useState(
    "/admin-dashboard/professional-profile"
  );
  const [showUserProfileLinks, setShowUserProfileLinks] = useState(true);
  const [showPreferencesLinks, setShowPreferencesLinks] = useState(false);

  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location.pathname]);

  const handleProfileOptimizationClick = () => {
    setShowUserProfileLinks(true);
    setShowPreferencesLinks(false);
  };

  const handlePreferencesClick = () => {
    setShowUserProfileLinks(false);
    setShowPreferencesLinks(true);
    setActiveLink("/admin-dashboard/professional-profile/credit-limit");
  };

  const handleSidebarTabs = () => {
    setShowAsideBar(false);
    setShowUserProfileLinks(false);
    setShowPreferencesLinks(false);
  };

  return (
    <div className="py-6 px-5 3xl:px-[30px] rounded-md border border-solid border-[#EBEFF3] bg-white">
      <div className="pb-5 mb-5 border-b border-solid border-[#EBEFF3]">
        <div className="mb-[30px]">
          <div className="flex items-center gap-[10px] mb-2">
            <h5 className="text-xs font-bold leading-[160%] text-customGray uppercase flex items-center justify-between gap-5 w-full">
              Profile complete {" "}
              <span>{professionalInfo?.data?.profile_percentage ?? 0}%</span>
            </h5>
          </div>
          <div aria-hidden="true">
            <div className="overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-[5px] rounded-full bg-[#7d34d8]"
                style={{ width: `${professionalInfo?.data?.profile_percentage ?? 0}%` }}
              />
            </div>
          </div>
        </div>
        <h6 className="text-xs font-bold leading-[160%] uppercase text-[#485868] mb-[14px]">
        Profile Settings
        </h6>
        <ul>
          <li className="mb-[14px]">
            <Link
              to="/admin-dashboard/professional-profile"
              state={{professionalId: professionalId}}
              className="text-base font-bold leading-[160%] text-customGray flex items-center gap-2.5 -tracking-[0.3px]"
              onClick={handleProfileOptimizationClick}
            >
              <UserIcon className="w-6 h-6 text-customGray" />
              Profile Optimization
            </Link>
            {showUserProfileLinks && (
              <ul className="pl-[34px] pt-[10px]">
                <li>
                  <Link
                    className={`text-base font-medium leading-[160%] text-[#485868] mb-[10px] ${
                      activeLink === "/admin-dashboard/professional-profile"
                        ? "text-primary"
                        : ""
                    }`}
                    to="/admin-dashboard/professional-profile"
                    state={{professionalId: professionalId}}
                    onClick={() => setShowAsideBar(false)}
                  >
                    User Profile
                  </Link>
                </li>
                <li>
                  <Link
                    className={`text-base font-medium leading-[160%] text-[#485868] mb-[10px] ${
                      activeLink === "/admin-dashboard/welcome-video"
                        ? "text-primary"
                        : ""
                    }`}
                    to="/admin-dashboard/welcome-video"
                    state={{professionalId: professionalId}}
                    onClick={() => setShowAsideBar(false)}
                  >
                    Welcome Video
                  </Link>
                </li>
                <li>
                  <Link
                    className={`text-base font-medium leading-[160%] text-[#485868] mb-[10px] ${
                      activeLink ===
                      "/admin-dashboard/professional-profile/reviews"
                        ? "text-primary"
                        : ""
                    }`}
                    to="/admin-dashboard/professional-profile/reviews"
                    state={{professionalId: professionalId}}
                    onClick={() => setShowAsideBar(false)}
                  >
                    Reviews
                  </Link>
                </li>
               <li>
                  <Link
                    className={`text-base font-medium leading-[160%] text-[#485868] mb-[10px] ${
                      activeLink ===
                      "/admin-dashboard/professional-profile/calendar-integration"
                        ? "text-primary"
                        : ""
                    }`}
                    to="/admin-dashboard/professional-profile/calendar-integration"
                    state={{professionalId: professionalId}}
                    onClick={() => setShowAsideBar(false)}
                  >
                    Integrations
                  </Link>
                </li>
              </ul>
            )}
          </li>
          <li>
            <Link
              className="text-base font-bold leading-[160%] text-customGray flex items-center gap-2.5"
              onClick={handlePreferencesClick}
              to="/admin-dashboard/professional-profile/credit-limit"
              state={{professionalId: professionalId}}
            >
              <Cog8ToothIcon className="w-6 h-6 text-customGray" />
              Preferences
            </Link>
            {showPreferencesLinks && (
              <ul className="mb-[14px] pl-[34px] pt-[10px]">
                <li>
                  <Link
                    className={`text-base font-medium leading-[160%] text-[#485868] mb-[10px] ${
                      activeLink ===
                      "/admin-dashboard/professional-profile/credit-limit"
                        ? "text-primary"
                        : ""
                    }`}
                    to="/admin-dashboard/professional-profile/credit-limit"
                    state={{professionalId: professionalId}}
                    onClick={() => setShowAsideBar(false)}
                  >
                    Credits, Leads Limit
                  </Link>
                </li>
                <li>
                  <Link
                    className={`text-base font-medium leading-[160%] text-[#485868] mb-[10px] ${
                      activeLink ===
                      "/admin-dashboard/professional-profile/region"
                        ? "text-primary"
                        : ""
                    }`}
                    to="/admin-dashboard/professional-profile/region"
                    state={{professionalId: professionalId}}
                    onClick={() => setShowAsideBar(false)}
                  >
                   Local Regions
                  </Link>
                </li>
                {professionalInfo?.data?.enable_virtual_sessions? <li>
                  <Link
                    className={`text-base font-medium leading-[160%] text-[#485868] ${
                      activeLink ===
                      "/admin-dashboard/professional-profile/states_license"
                        ? "text-primary"
                        : ""
                    }`}
                    to="/admin-dashboard/professional-profile/states_license"
                    state={{professionalId: professionalId}}
                    onClick={() => setShowAsideBar(false)}
                  >
                    Virtual Regions
                  </Link>
                </li>:null}
              </ul>
            )}
          </li>
        </ul>
      </div>
      <div className="pb-5 mb-5 border-b border-solid border-[#EBEFF3]">
        <h6 className="text-xs font-bold leading-[160%] uppercase text-[#485868] mb-[14px]">
        Instantengage™ settings
        </h6>
        <ul>
          <li>
            <Link
              className={`text-base font-bold leading-[160%] text-customGray flex items-center gap-2.5 mb-[14px] ${
                activeLink ===
                "/admin-dashboard/professional-profile/sms-template"
                  ? "text-primary"
                  : ""
              }`}
              onClick={handleSidebarTabs}
              to="/admin-dashboard/professional-profile/sms-template"
              state={{professionalId: professionalId}}
            >
              <span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                >
                  <g clipPath="url(#clip0_949_40113)">
                    <path
                      d="M11 3.27637H21V11.2764H18L14 13.2764V11.2764H11V3.27637Z"
                      stroke="#1C1C1C"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M15 16.2764V20.2764C15 20.5416 14.8946 20.7959 14.7071 20.9835C14.5196 21.171 14.2652 21.2764 14 21.2764H6C5.73478 21.2764 5.48043 21.171 5.29289 20.9835C5.10536 20.7959 5 20.5416 5 20.2764V6.27637C5 6.01115 5.10536 5.7568 5.29289 5.56926C5.48043 5.38172 5.73478 5.27637 6 5.27637H8"
                      stroke="#1C1C1C"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 18.2764V18.2864"
                      stroke="#1C1C1C"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_949_40113">
                      <rect
                        width="24"
                        height="24"
                        fill="white"
                        transform="translate(0 0.276367)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </span>
              InstantEngage™ SMS
            </Link>
          </li>
          <li>
            <Link
              className={`text-base font-bold leading-[160%] text-customGray flex items-center gap-2.5 ${
                activeLink ===
                "/admin-dashboard/professional-profile/email-template"
                  ? "text-primary"
                  : ""
              }`}
              onClick={handleSidebarTabs}
              to="/admin-dashboard/professional-profile/email-template"
              state={{professionalId: professionalId}}
            >
              <span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                >
                  <g clipPath="url(#clip0_949_40117)">
                    <path
                      d="M4 5.27637C4 5.01115 4.10536 4.7568 4.29289 4.56926C4.48043 4.38172 4.73478 4.27637 5 4.27637H19C19.2652 4.27637 19.5196 4.38172 19.7071 4.56926C19.8946 4.7568 20 5.01115 20 5.27637V7.27637C20 7.54158 19.8946 7.79594 19.7071 7.98347C19.5196 8.17101 19.2652 8.27637 19 8.27637H5C4.73478 8.27637 4.48043 8.17101 4.29289 7.98347C4.10536 7.79594 4 7.54158 4 7.27637V5.27637Z"
                      stroke="#1C1C1C"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4 13.2764C4 13.0112 4.10536 12.7568 4.29289 12.5693C4.48043 12.3817 4.73478 12.2764 5 12.2764H9C9.26522 12.2764 9.51957 12.3817 9.70711 12.5693C9.89464 12.7568 10 13.0112 10 13.2764V19.2764C10 19.5416 9.89464 19.7959 9.70711 19.9835C9.51957 20.171 9.26522 20.2764 9 20.2764H5C4.73478 20.2764 4.48043 20.171 4.29289 19.9835C4.10536 19.7959 4 19.5416 4 19.2764V13.2764Z"
                      stroke="#1C1C1C"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14 12.2764H20"
                      stroke="#1C1C1C"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14 16.2764H20"
                      stroke="#1C1C1C"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14 20.2764H20"
                      stroke="#1C1C1C"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_949_40117">
                      <rect
                        width="24"
                        height="24"
                        fill="white"
                        transform="translate(0 0.276367)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </span>
              InstantEngage™ Email
            </Link>
          </li>
        </ul>
      </div>
      <div className="pb-5 mb-5 border-b border-solid border-[#EBEFF3]">
        <h6 className="text-xs font-bold leading-[160%] uppercase text-[#485868] mb-[14px]">
          Payment Methods
        </h6>
        <ul>
          <li>
            <Link
              className={`text-base font-bold leading-[160%] text-customGray flex items-center gap-2.5 ${
                activeLink ===
                "/admin-dashboard/professional-profile/billing-payment"
                  ? "text-primary"
                  : ""
              }`}
              onClick={handleSidebarTabs}
              to="/admin-dashboard/professional-profile/billing-payment"
              state={{professionalId: professionalId}}
            >
              <DocumentTextIcon className="w-6 h-6 text-customGray" />
              Billing & Invoices
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ProfessionalAdminSettingsAsideBar;
